import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useSnackbar } from 'notistack';
import {
  Box,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Divider,
  CircularProgress,
  Alert,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import { getKpiList } from '../../modules/targetModule/targetKpi';
import { getUserTarget, setUserTarget, resetUserTarget } from '../../modules/targetModule/userTarget';
import * as targetApi from '../../lib/api/target';
import * as employeeApi from '../../lib/api/employee';
import TargetTable from '../../components/targetmng/TargetTable';

/**
 * 목표 설정 폼 컨테이너
 */
const TargetFormContainer = () => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { targetTyp, deptId } = useParams();
  
  // 상태
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedUser, setSelectedUser] = useState('');
  const [deptUsers, setDeptUsers] = useState([]);
  const [monthlyData, setMonthlyData] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  
  // 연도 옵션 (현재년도 기준 -2년 ~ +1년)
  const years = useMemo(() => {
    const result = [];
    for (let i = currentYear - 2; i <= currentYear + 1; i++) {
      result.push(i);
    }
    return result;
  }, [currentYear]);
  
  // Redux 상태
  const { kpiList, userTarget, loading } = useSelector(({ targetModule, loading }) => ({
    kpiList: targetModule.targetKpi.kpiList,
    userTarget: targetModule.userTarget.userTarget,
    loading: loading['target/GET_USER_TARGET'],
  }));

  // 목표 유형명
  const targetTypeName = useMemo(() => {
    const names = {
      SALES: '영업 목표',
      PRODUCTION: '생산 목표',
      TECH: '기술 목표',
      QC: '품질 목표',
    };
    return names[targetTyp] || '목표';
  }, [targetTyp]);

  // 부서원 목록 조회
  const fetchDeptUsers = useCallback(async (deptUuid) => {
    if (!deptUuid) return;
    
    setLoadingUsers(true);
    try {
      const res = await employeeApi.getEmployees({ dptUuid: deptUuid });
      if (res.data?.list) {
        setDeptUsers(res.data.list);
      } else {
        setDeptUsers([]);
      }
    } catch (err) {
      console.error('부서원 조회 실패:', err);
      setDeptUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  }, []);

  // KPI 목록 조회
  useEffect(() => {
    if (targetTyp) {
      dispatch(getKpiList({ targetTyp, useYn: 'Y' }));
    }
  }, [dispatch, targetTyp]);

  // 부서 변경 시 부서원 목록 조회
  useEffect(() => {
    if (deptId) {
      fetchDeptUsers(deptId);
      setSelectedUser('');
      dispatch(resetUserTarget());
    }
  }, [deptId, fetchDeptUsers, dispatch]);

  // 사용자 목표 조회
  useEffect(() => {
    if (targetTyp && selectedUser && selectedYear) {
      dispatch(getUserTarget({ 
        targetTyp, 
        usrUuid: selectedUser, 
        targetYear: String(selectedYear) 
      }));
    } else {
      dispatch(resetUserTarget());
    }
  }, [dispatch, targetTyp, selectedUser, selectedYear]);

  // 목표 데이터 초기화 (12개월)
  useEffect(() => {
    if (kpiList.length > 0) {
      const initialData = [];
      for (let month = 1; month <= 12; month++) {
        const monthData = { month, kpiValues: {} };
        kpiList.forEach((kpi) => {
          monthData.kpiValues[kpi.kpiCd] = 0;
        });
        initialData.push(monthData);
      }
      
      // 기존 데이터가 있으면 매핑
      if (userTarget?.monthlyTargets) {
        userTarget.monthlyTargets.forEach((mt) => {
          const idx = initialData.findIndex((d) => d.month === mt.month);
          if (idx !== -1 && mt.kpiValues) {
            Object.keys(mt.kpiValues).forEach((kpiCd) => {
              initialData[idx].kpiValues[kpiCd] = Number(mt.kpiValues[kpiCd]) || 0;
            });
          }
        });
      }
      
      setMonthlyData(initialData);
    }
  }, [kpiList, userTarget]);

  // 값 변경 핸들러
  const handleValueChange = useCallback((month, kpiCd, value) => {
    setMonthlyData((prev) =>
      prev.map((item) =>
        item.month === month
          ? {
              ...item,
              kpiValues: {
                ...item.kpiValues,
                [kpiCd]: Number(value) || 0,
              },
            }
          : item
      )
    );
  }, []);

  // 저장
  const handleSave = async () => {
    if (!selectedUser) {
      enqueueSnackbar('사용자를 선택하세요', { variant: 'warning' });
      return;
    }

    setSaving(true);
    try {
      const requestData = {
        targetTyp,
        usrUuid: selectedUser,
        targetYear: String(selectedYear),
        monthlyTargets: monthlyData.map((item) => ({
          month: item.month,
          kpiValues: Object.entries(item.kpiValues).map(([kpiCd, targetVal]) => ({
            kpiCd,
            targetVal: String(targetVal),
          })),
        })),
      };

      const res = await targetApi.saveTarget(requestData);
      if (res.data?.result === 'success') {
        dispatch(setUserTarget(res.data.data));
        enqueueSnackbar('목표가 저장되었습니다', { variant: 'success' });
      }
    } catch (err) {
      const message = err.response?.data?.message || err.message;
      enqueueSnackbar(`저장 실패: ${message}`, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // 부서 미선택
  if (!deptId) {
    return (
      <Paper elevation={2} sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">
          좌측 조직도에서 부서를 선택하세요
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper elevation={2} sx={{ p: 2 }}>
      {/* 헤더 */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6">{targetTypeName} 설정</Typography>
      </Box>

      <Divider sx={{ mb: 2 }} />

      {/* 필터 영역 */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        {/* 연도 선택 */}
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>연도</InputLabel>
          <Select
            value={selectedYear}
            label="연도"
            onChange={(e) => setSelectedYear(e.target.value)}
          >
            {years.map((year) => (
              <MenuItem key={year} value={year}>
                {year}년
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* 사용자 선택 */}
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>사용자</InputLabel>
          <Select
            value={selectedUser}
            label="사용자"
            onChange={(e) => setSelectedUser(e.target.value)}
            disabled={loadingUsers}
          >
            <MenuItem value="">
              <em>선택하세요</em>
            </MenuItem>
            {deptUsers.map((user) => (
              <MenuItem key={user.usrUuid} value={user.usrUuid}>
                {user.usrNm} {user.gradeNm && `(${user.gradeNm})`}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {loadingUsers && <CircularProgress size={24} />}

        {/* 저장 버튼 */}
        <Box sx={{ ml: 'auto' }}>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
            onClick={handleSave}
            disabled={saving || !selectedUser}
          >
            저장
          </Button>
        </Box>
      </Box>

      {/* 안내 메시지 */}
      {!selectedUser && (
        <Alert severity="info" sx={{ mb: 2 }}>
          사용자를 선택하면 월별 목표를 입력할 수 있습니다.
        </Alert>
      )}

      <Divider sx={{ mb: 2 }} />

      {/* 목표 테이블 */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : kpiList.length === 0 ? (
        <Alert severity="warning">
          해당 목표 유형({targetTypeName})의 KPI가 정의되지 않았습니다.
          <br />
          관리자에게 KPI 설정을 요청하세요.
        </Alert>
      ) : (
        <TargetTable
          kpiList={kpiList}
          monthlyData={monthlyData}
          onValueChange={handleValueChange}
          disabled={!selectedUser}
        />
      )}
    </Paper>
  );
};

export default TargetFormContainer;
